/**
 * Global PAE (Pre-Analytical Error) reporter.
 *
 * • Floating button bottom-right of every authenticated page.
 * • Click → modal: pick error type, add notes, submit.
 * • Auto-captures the page URL + a friendly page label, plus any
 *   ?id= or :id route param so the error can be tied back to the
 *   patient/order/specimen/etc. that prompted it.
 * • Hidden on /login and /tech-app (interferes with mobile UX).
 *
 * Mounted once in MainLayout so every authenticated route sees it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, matchPath } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader, X } from 'lucide-react';
import api from '../../services/api';
import { usePAEContext } from '../../context/PAEContext';

// A Pre-Analytical Error only makes sense on the specimen-lifecycle pages —
// patient identification, order entry, requisitions, specimen collection /
// labeling / tracking, accessioning, phlebotomy, and result review. Show the
// floating reporter only on these; hide it everywhere else (billing/RCM,
// admin, settings, dashboards, etc.).
const RELEVANT_PATHS = [
  /^\/patient(s|s-manager)?(\/|$)/i,
  /^\/orders(-manager)?(\/|$)/i,
  /^\/requisitions(\/|$)/i,
  /^\/results(-manager)?(\/|$)/i,
  /^\/specimens(\/|$)/i,
  /^\/accessioning(\/|$)/i,
  /^\/phlebotomy-log(\/|$)/i,
];

// Map common URL patterns → friendly label + the kind of context the
// :id route param refers to. Falls back to the raw pathname.
const PAGE_MAP = [
  { pattern: '/patients/:id',       label: 'Patient Detail',      ctx: 'patient_id'     },
  { pattern: '/patients',           label: 'Patients',            ctx: null              },
  { pattern: '/orders/:id',         label: 'Order Detail',        ctx: 'order_id'       },
  { pattern: '/orders',             label: 'Orders',              ctx: null              },
  { pattern: '/specimens/:id',      label: 'Specimen Detail',     ctx: 'specimen_id'    },
  { pattern: '/specimens',          label: 'Specimens',           ctx: null              },
  { pattern: '/requisitions/:id',   label: 'Requisition Detail',  ctx: 'requisition_id' },
  { pattern: '/requisitions',       label: 'Requisitions',        ctx: null              },
  { pattern: '/results',            label: 'Results',             ctx: null              },
  { pattern: '/accessioning',       label: 'Accessioning',        ctx: null              },
  { pattern: '/clients/:id',        label: 'Client Detail',       ctx: 'client_id'      },
  { pattern: '/clients',            label: 'Clients',             ctx: null              },
  { pattern: '/rcm/claims/:id',     label: 'Claim Detail',        ctx: 'claim_id'       },
];

function describeRoute(pathname, search) {
  for (const m of PAGE_MAP) {
    const match = matchPath({ path: m.pattern, end: true }, pathname);
    if (match) {
      const idVal = match.params?.id;
      const ctx = (m.ctx && idVal) ? { [m.ctx]: idVal } : {};
      return { label: m.label, ctx };
    }
  }
  // Fall back: try to extract ?id=... from the search string.
  const sp = new URLSearchParams(search || '');
  const qid = sp.get('id');
  if (qid) {
    if (pathname.includes('/patients'))      return { label: 'Patients',     ctx: { patient_id: qid } };
    if (pathname.includes('/orders'))        return { label: 'Orders',       ctx: { order_id:   qid } };
    if (pathname.includes('/specimens'))     return { label: 'Specimens',    ctx: { specimen_id: qid } };
    if (pathname.includes('/requisitions'))  return { label: 'Requisitions', ctx: { requisition_id: qid } };
  }
  return { label: pathname.replace(/^\//, '') || 'Home', ctx: {} };
}

export default function PAEButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const relevant = RELEVANT_PATHS.some(rx => rx.test(location.pathname));
  if (!relevant) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Report a Pre-Analytical Error (PAE)"
        aria-label="Report a Pre-Analytical Error"
        style={fabStyle}
      >
        <AlertTriangle size={14} />
      </button>
      {open && <PAEModal onClose={() => setOpen(false)} />}
    </>
  );
}

const fabStyle = {
  position: 'fixed', right: 12, bottom: 12, zIndex: 9000,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, padding: 0, borderRadius: 999,
  background: '#dc2626', color: '#fff',
  border: 'none', boxShadow: '0 3px 8px rgba(220, 38, 38, 0.35)',
  cursor: 'pointer', opacity: 0.85,
};

function PAEModal({ onClose }) {
  const location = useLocation();
  const pageCtx  = usePAEContext();   // pushed by whatever page is open
  const [types, setTypes]     = useState([]);
  const [typeId, setTypeId]   = useState('');
  const [desc, setDesc]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [done, setDone]       = useState(null);    // {ok, msg}
  const taRef = useRef(null);

  // ── Resolve initial attach context ──────────────────────────────
  // Priority: in-page push (PAEContext) > URL route params > URL ?id=
  const urlCtx = describeRoute(location.pathname, location.search);
  const initialAttach = {
    order_id:       pageCtx?.order_id       ?? urlCtx.ctx.order_id       ?? '',
    requisition_id: pageCtx?.requisition_id ?? urlCtx.ctx.requisition_id ?? '',
    claim_id:       pageCtx?.claim_id       ?? urlCtx.ctx.claim_id       ?? '',
    patient_id:     pageCtx?.patient_id     ?? urlCtx.ctx.patient_id     ?? '',
    specimen_id:    pageCtx?.specimen_id    ?? urlCtx.ctx.specimen_id    ?? '',
    client_id:      pageCtx?.client_id      ?? urlCtx.ctx.client_id      ?? '',
  };
  const [attach, setAttach] = useState(initialAttach);
  // The "auto-attached" label, if any, used for the green chip.
  const autoLabel = pageCtx?.label
    || (urlCtx.label !== 'Home' && Object.keys(urlCtx.ctx).length ? urlCtx.label : null);

  // Pull error types on open.
  useEffect(() => {
    api.get('/api/pae/types')
      .then(r => {
        const list = r?.data?.types || [];
        setTypes(list);
        if (list.length && !typeId) setTypeId(list[0].id);
      })
      .catch(() => setDone({ ok: false, msg: "Couldn't load error types." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus the description box for fast capture.
  useEffect(() => { taRef.current?.focus(); }, []);

  const submit = useCallback(async () => {
    if (!typeId) { setDone({ ok: false, msg: 'Pick an error type.' }); return; }
    const payload = {
      error_type_id: Number(typeId),
      description: desc || null,
      page_url: location.pathname + (location.search || ''),
      page_label: pageCtx?.label || urlCtx.label,
    };
    // Coerce manual inputs back into integers (UUIDs stay strings).
    for (const k of ['order_id', 'requisition_id', 'claim_id', 'patient_id', 'specimen_id']) {
      const v = attach[k];
      if (v !== '' && v != null) {
        const n = Number(v);
        if (!Number.isNaN(n)) payload[k] = n;
      }
    }
    if (attach.client_id) payload.client_id = String(attach.client_id);

    setBusy(true);
    try {
      await api.post('/api/pae/errors', payload);
      setDone({ ok: true, msg: 'Logged.' });
      setTimeout(onClose, 900);
    } catch (e) {
      setDone({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  }, [typeId, desc, attach, pageCtx, urlCtx.label, location, onClose]);

  const sel = types.find(t => Number(t.id) === Number(typeId));
  const sevColor = SEV_COLORS[sel?.severity || 'MEDIUM'];

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={modal}>
        <div style={modalHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} color="#dc2626" />
            <strong style={{ fontSize: 15 }}>Report a Pre-Analytical Error</strong>
          </div>
          <button onClick={onClose} style={iconBtn}><X size={14} /></button>
        </div>

        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Auto-attach chip — appears only when context was detected */}
          {autoLabel && (
            <div style={{
              padding: '6px 10px', borderRadius: 6,
              background: '#d1fae5', color: '#047857',
              fontSize: 12, fontWeight: 600, display: 'flex',
              alignItems: 'center', gap: 6,
            }}>
              <CheckCircle2 size={14} /> Attaching to <strong>{autoLabel}</strong>
            </div>
          )}

          <PageHint location={location} />

          {/* Manual "Attach to" inputs — pre-filled from context but editable. */}
          <AttachInputs attach={attach} setAttach={setAttach} />

          <Field label="Error Type">
            <select value={typeId} onChange={e => setTypeId(e.target.value)} style={input}>
              {types.length === 0 && <option value="">(loading…)</option>}
              {types.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.severity ? `· ${t.severity}` : ''}
                </option>
              ))}
            </select>
          </Field>

          {sel && (
            <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
              <span style={{ ...pill, background: sevColor.bg, color: sevColor.fg }}>
                {sel.severity}
              </span>
              <span style={{ ...pill, background: '#eef2ff', color: '#4338ca' }}>
                Routes to {sel.default_owner.replace('_', ' ')}
              </span>
            </div>
          )}

          <Field label="Notes">
            <textarea ref={taRef} value={desc} onChange={e => setDesc(e.target.value)}
                      rows={4}
                      placeholder="What happened? Tube color, label issue, sample condition, etc."
                      style={{ ...input, resize: 'vertical', minHeight: 80 }} />
          </Field>

          {done && (
            <div style={{
              padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: done.ok ? '#d1fae5' : '#fee2e2',
              color:      done.ok ? '#047857' : '#b91c1c',
            }}>
              {done.ok ? <CheckCircle2 size={12} style={{ verticalAlign: -2, marginRight: 4 }} /> : null}
              {done.msg}
            </div>
          )}
        </div>

        <div style={modalFoot}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={submit} disabled={busy || !typeId} style={{
            ...primaryBtn, cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}>
            {busy ? <Loader size={14} className="spin" /> : <AlertTriangle size={14} />}
            Log Error
          </button>
        </div>
      </div>
    </div>
  );
}

function PageHint({ location }) {
  const { label } = describeRoute(location.pathname, location.search);
  return (
    <div style={{
      fontSize: 11, color: 'var(--text-secondary, #64748b)',
      padding: '6px 8px', background: 'var(--surface, #f8fafc)',
      border: '1px solid var(--divider, #e2e8f0)', borderRadius: 6,
    }}>
      Reporting from <strong>{label}</strong>{' '}
      <span style={{ fontFamily: 'monospace', opacity: 0.7 }}>
        {location.pathname}{location.search}
      </span>
    </div>
  );
}

function AttachInputs({ attach, setAttach }) {
  const set = (k) => (e) => setAttach({ ...attach, [k]: e.target.value });
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--text-secondary, #64748b)',
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
      }}>Attach to (optional)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <SmallField label="Order #">
          <input value={attach.order_id || ''} onChange={set('order_id')}
                 placeholder="123" style={smallInput} />
        </SmallField>
        <SmallField label="Requisition #">
          <input value={attach.requisition_id || ''} onChange={set('requisition_id')}
                 placeholder="456" style={smallInput} />
        </SmallField>
        <SmallField label="Claim #">
          <input value={attach.claim_id || ''} onChange={set('claim_id')}
                 placeholder="789" style={smallInput} />
        </SmallField>
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-secondary, #64748b)', marginTop: 4 }}>
        Auto-filled when an order, requisition, or claim is open.
      </div>
    </div>
  );
}

function SmallField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, fontWeight: 700,
                      color: 'var(--text-secondary, #64748b)',
                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const smallInput = {
  padding: '4px 8px', fontSize: 12,
  border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 4,
  background: 'var(--bg, #fff)', color: 'var(--text, #0f172a)',
  width: '100%', boxSizing: 'border-box',
};

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700,
                      color: 'var(--text-secondary, #64748b)',
                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

// Convert string ids from route params into integers where the API expects them.
function numericContext(ctx) {
  const out = {};
  for (const [k, v] of Object.entries(ctx || {})) {
    if (k === 'client_id') out[k] = String(v); // client ids are UUID strings
    else {
      const n = Number(v);
      if (!Number.isNaN(n)) out[k] = n;
    }
  }
  return out;
}

const SEV_COLORS = {
  LOW:    { bg: '#ecfdf5', fg: '#047857' },
  MEDIUM: { bg: '#fef3c7', fg: '#92400e' },
  HIGH:   { bg: '#fee2e2', fg: '#b91c1c' },
};

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9500, padding: 20,
};
const modal = {
  background: 'var(--bg, #fff)', color: 'var(--text, #0f172a)',
  borderRadius: 10, width: 'min(520px, 100%)',
  maxHeight: '90vh', overflow: 'auto',
  boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
};
const modalHead = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 14px', borderBottom: '1px solid var(--divider, #e2e8f0)',
};
const modalFoot = {
  display: 'flex', justifyContent: 'flex-end', gap: 8,
  padding: '10px 14px', borderTop: '1px solid var(--divider, #e2e8f0)',
};
const input = {
  padding: '6px 10px', fontSize: 14,
  border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 6,
  background: 'var(--bg, #fff)', color: 'var(--text, #0f172a)',
  width: '100%', boxSizing: 'border-box',
};
const ghostBtn = {
  padding: '6px 12px', fontSize: 13, fontWeight: 600,
  background: 'transparent', color: 'var(--text-secondary, #64748b)',
  border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 6, cursor: 'pointer',
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', fontSize: 13, fontWeight: 700,
  background: '#dc2626', color: '#fff',
  border: '1px solid #dc2626', borderRadius: 6,
};
const iconBtn = {
  display: 'inline-flex', alignItems: 'center',
  padding: 6, background: 'transparent',
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: 6, cursor: 'pointer',
  color: 'var(--text-secondary, #64748b)',
};
const pill = { padding: '2px 8px', borderRadius: 999, fontWeight: 700 };
